import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { toast } from "sonner";
import { LayoutTemplate, Trash2, Plus, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [studies, setStudies] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [history, setHistory] = useState("");
  const [priority, setPriority] = useState("Routine");
  const [selected, setSelected] = useState([]);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/studies").then((r) => setStudies(r.data)); }, []);

  const allStudies = Object.entries(studies).flatMap(([cat, items]) => items.map((i) => ({ category: cat, name: i.name })));
  const toggle = (s) => setSelected((p) => p.find((x) => x.name === s.name) ? p.filter((x) => x.name !== s.name) : [...p, s]);

  const create = async () => {
    if (!name.trim()) return toast.error("Name required");
    try {
      await api.post("/templates", { name, studies: selected, priority, clinical: { clinical_history: history } });
      toast.success("Template saved");
      setOpen(false); setName(""); setHistory(""); setSelected([]); load();
    } catch (e) { toast.error("Could not save template"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/templates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-head">Saved Templates</h1>
          <p className="text-slate-500 mt-1">Prefill examinations and clinical wording in one tap.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-template-btn" className="rounded-xl h-12 px-5 bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" /> New Template</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head">Create Template</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Template Name</Label><Input data-testid="template-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl" placeholder="e.g. Chest X-Ray" /></div>
              <div><Label>Default Clinical History</Label><Textarea data-testid="template-history" value={history} onChange={(e) => setHistory(e.target.value)} className="mt-1.5 rounded-xl" /></div>
              <div>
                <Label>Studies</Label>
                <div className="mt-2 flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {allStudies.map((s) => (
                    <button key={s.name} type="button" data-testid={`tpl-study-${s.name.replace(/[^a-zA-Z0-9]/g, "-")}`} onClick={() => toggle(s)} className={`text-xs px-3 py-1.5 rounded-full border ${selected.find((x) => x.name === s.name) ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600"}`}>{s.name}</button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="save-template-btn" onClick={create} className="rounded-xl bg-blue-600 hover:bg-blue-700">Save Template</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {templates.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 group">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center"><LayoutTemplate className="w-5 h-5 text-blue-600" /></div>
                {!t.is_global && <button data-testid={`del-template-${t.id}`} onClick={() => remove(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <h3 className="font-semibold text-slate-900 font-head mt-3">{t.name}{t.is_global && <span className="text-xs text-blue-500 ml-2">Global</span>}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{t.studies?.map((s) => s.name).join(", ")}</p>
              <Button data-testid={`use-template-${t.id}`} onClick={() => navigate(`/referrals/new?template=${t.id}`)} variant="outline" className="w-full mt-4 rounded-xl border-slate-200">Use template <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </div>
          ))}
          {templates.length === 0 && <div className="col-span-full text-center text-slate-400 py-16">No templates yet.</div>}
        </div>
      )}
    </div>
  );
}
